import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { FormCheckbox, FormField, FormSelect } from '../../components/FormField'
import { Breadcrumbs, type Crumb } from '../../components/Breadcrumbs'
import { ActivationPanel } from '../../components/ActivationPanel'
import { RecordLink } from '../../components/RecordLink'
import { FleetAvailabilityNote } from './FleetStandingCell'
import type { CarrierStanding, FleetStanding } from './fleetStanding'
import { DocumentsPanel } from '../documents/DocumentsPanel'
import { apiErrorMessage } from '../../lib/errors'
import { keepDecimal, parseAmount } from '../../lib/number'
import {
  addTruck,
  BOX_LENGTHS,
  featuresAllowedOn,
  getCarrier,
  updateTruck,
  STRAIGHT_BOX_TRUCK,
  TRUCK_TYPES,
  type EquipmentFeature,
  type TruckPayload,
  type TruckView,
} from './api'
import {
  messageFor,
  toTruckFormErrors,
  withoutTruckErrorsFor,
  type FormErrors,
} from './errors'
import { CHANGED_ELSEWHERE } from '../../lib/formErrors'
import { ChangedElsewhereNotice } from '../../components/ChangedElsewhereNotice'

// A new truck starts as a sleeper cab: Hugo's fleets run mostly those (TRKC-102).
const emptyForm = {
  unitNumber: '',
  type: 'SleeperCab',
  make: '',
  model: '',
  vin: '',
  licensePlate: '',
  palletCapacity: '',
  weightCapacity: '',
  boxLength: '',
}

type TruckForm = typeof emptyForm

/**
 * A saved truck in the form's terms — its room, features and box only ever a straight box
 * truck's. A truck saved before cabs were asked holds none, and the select says so.
 */
function formOf(truck: TruckView): TruckForm {
  return {
    unitNumber: truck.unitNumber,
    type: truck.type ?? '',
    make: truck.make ?? '',
    model: truck.model ?? '',
    vin: truck.vin ?? '',
    licensePlate: truck.licensePlate ?? '',
    palletCapacity: truck.palletCapacity === null ? '' : String(truck.palletCapacity),
    weightCapacity: truck.weightCapacityPounds === null ? '' : String(truck.weightCapacityPounds),
    boxLength: truck.boxLengthFeet === null ? '' : String(truck.boxLengthFeet),
  }
}

const noErrors: FormErrors = { fields: {}, summary: [] }

/** Adds or edits a truck on a carrier's fleet; the carrier page is the way back. */
export function TruckFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: carrierId, truckId } = useParams()
  const isEditing = truckId !== undefined

  const [form, setForm] = useState<TruckForm>(emptyForm)
  // A straight box truck's features (TRKC-100) — a set, beside the text fields.
  const [features, setFeatures] = useState<EquipmentFeature[]>([])
  const [errors, setErrors] = useState<FormErrors>(noErrors)
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  const [isLoading, setIsLoading] = useState(isEditing)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // The reading this form was opened on. Set when the record loads and by "load what is
  // saved" — never by the standing re-read below, which would adopt somebody else's
  // version and let the next save overwrite what they wrote.
  const [version, setVersion] = useState<string | null>(null)
  // The trail's names (TRKC-97): the carrier's, and this record's as it is saved —
  // never as it is being typed.
  const [carrierName, setCarrierName] = useState<string | null>(null)
  const [savedName, setSavedName] = useState<string | null>(null)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)
  // Whether we still work with this one. Its own fact, stated by its own button --
  // never a field that rides along with a save of the name and the phone.
  // The carrier's half of the answer. Held rather than inferred: a record whose own
  // papers are incomplete under a carrier we stood down is out of play twice over,
  // and the two are indistinguishable from the child's own fields.
  const [standing, setStanding] = useState<{
    record: FleetStanding
    carrier: CarrierStanding
  } | null>(null)
  const [activation, setActivation] = useState<{ active: boolean; deactivatedAt: string | null }>({
    active: true,
    deactivatedAt: null,
  })

  // The re-read in flight: only the latest may write (see DriverFormPage).
  const refreshGeneration = useRef(0)
  // A re-read that failed — the form stays, the note goes (see DriverFormPage).
  const [standingFailure, setStandingFailure] = useState<{ error: unknown } | null>(null)

  /**
   * The availability note is the server's conclusion, re-read after every write that
   * can move it — an activation change, a paper filed or struck in the panel below —
   * and never guessed at. Re-read on its own, without the form: the truck's fields did
   * not change, and a VIN half typed must not be thrown away because a registration
   * was filed underneath it (review finding on #13).
   */
  const refreshStanding = useCallback(async () => {
    if (carrierId === undefined || truckId === undefined) {
      return
    }

    refreshGeneration.current += 1
    const generation = refreshGeneration.current
    setStandingFailure(null)

    try {
      const carrier = await getCarrier(carrierId)
      if (generation !== refreshGeneration.current) {
        return
      }

      const truck = carrier.trucks.find((row) => row.id === truckId)
      if (truck === undefined) {
        setLoadFailure({ error: null })
        return
      }

      setActivation({ active: truck.active, deactivatedAt: truck.deactivatedAt })
      setStanding({
        record: truck,
        carrier,
      })
    } catch (caught) {
      if (generation === refreshGeneration.current) {
        setStanding(null)
        setStandingFailure({ error: caught })
      }
    }
  }, [carrierId, truckId])

  useEffect(() => {
    if (carrierId === undefined || truckId === undefined) {
      return
    }

    let active = true
    refreshGeneration.current += 1
    setStandingFailure(null)
    setIsLoading(true)
    setLoadFailure(null)

    void (async () => {
      try {
        const carrier = await getCarrier(carrierId)
        const truck = carrier.trucks.find((row) => row.id === truckId)
        if (!active) {
          return
        }

        if (truck === undefined) {
          setLoadFailure({ error: null })
          return
        }

        setActivation({ active: truck.active, deactivatedAt: truck.deactivatedAt })
        setStanding({
          record: truck,
          carrier,
        })
        setVersion(truck.updatedAt)
        setCarrierName(carrier.name)
        setSavedName(truck.unitNumber)
        setForm(formOf(truck))
        setFeatures(truck.features)
      } catch (caught) {
        if (active) {
          setLoadFailure({ error: caught })
        }
      } finally {
        if (active) {
          setIsLoading(false)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [carrierId, truckId])

  // A new truck reads nothing else, so its carrier's name is asked for the trail
  // alone. Unread, the step shows an ellipsis: the form works the same without it.
  useEffect(() => {
    if (carrierId === undefined || isEditing) {
      return
    }

    let active = true

    getCarrier(carrierId)
      .then((carrier) => {
        if (active) {
          setCarrierName(carrier.name)
        }
      })
      .catch(() => {})

    return () => {
      active = false
    }
  }, [carrierId, isEditing])

  if (carrierId === undefined) {
    return null
  }

  function set(field: keyof TruckForm, value: string) {
    setForm((current) => {
      const next = { ...current, [field]: value }
      // A truck that stops being a straight box truck lets go of its room and its box: from
      // then on its trailer states them, and the server refuses a figure nobody would read.
      return field === 'type' && value !== STRAIGHT_BOX_TRUCK
        ? { ...next, palletCapacity: '', weightCapacity: '', boxLength: '' }
        : next
    })
    setErrors((current) => withoutTruckErrorsFor(current, field))

    // A feature the new type cannot have goes with the old type — every one, off a box truck.
    if (field === 'type') {
      const allowed = value === STRAIGHT_BOX_TRUCK ? featuresAllowedOn(STRAIGHT_BOX_TRUCK) : []
      setFeatures((current) => current.filter((feature) => allowed.includes(feature)))
    }
  }

  function toggleFeature(feature: EquipmentFeature, checked: boolean) {
    setFeatures((current) =>
      checked ? [...current, feature] : current.filter((existing) => existing !== feature),
    )
    setErrors((current) => withoutTruckErrorsFor(current, 'features'))
  }

  /**
   * The deliberate step after a refusal: this truck as it is stored now, into this
   * form. Nothing else on the page is reloaded, and the version comes with it.
   */
  async function loadWhatIsSaved() {
    if (carrierId === undefined || truckId === undefined) {
      return
    }

    setIsLoadingSaved(true)

    try {
      const carrier = await getCarrier(carrierId)
      const truck = carrier.trucks.find((row) => row.id === truckId)

      if (truck === undefined) {
        setLoadFailure({ error: null })
        return
      }

      setVersion(truck.updatedAt)
      setCarrierName(carrier.name)
      setSavedName(truck.unitNumber)
      setForm(formOf(truck))
      setFeatures(truck.features)
      setErrors(noErrors)
    } catch (caught) {
      setErrors(toTruckFormErrors(caught))
    } finally {
      setIsLoadingSaved(false)
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors(noErrors)

    // A straight box truck's room, read the trailer form's way: empty means "not recorded",
    // anything typed must be a real number — the pallets a whole one. Any other truck's boxes
    // are empty: changing the type away from a box truck emptied them, room and features alike.
    const pallets = form.palletCapacity.trim() === '' ? null : parseAmount(form.palletCapacity)
    if (form.palletCapacity.trim() !== '' && (pallets === null || !Number.isInteger(pallets))) {
      setErrors({ fields: { palletCapacity: 'invalid_pallets' }, summary: [] })
      return
    }

    const weight = form.weightCapacity.trim() === '' ? null : parseAmount(form.weightCapacity)
    if (form.weightCapacity.trim() !== '' && weight === null) {
      setErrors({ fields: { weightCapacity: 'invalid_weight' }, summary: [] })
      return
    }

    const payload: TruckPayload = {
      unitNumber: form.unitNumber.trim(),
      type: form.type,
      make: blankToNull(form.make),
      model: blankToNull(form.model),
      vin: blankToNull(form.vin),
      licensePlate: blankToNull(form.licensePlate),
      palletCapacity: pallets,
      weightCapacityPounds: weight,
      features,
      // One of the select's four lengths, or none — the server asks a box truck for one.
      boxLengthFeet: form.boxLength === '' ? null : Number(form.boxLength),
      version: version ?? undefined,
    }

    setIsSubmitting(true)

    try {
      if (truckId === undefined) {
        await addTruck(carrierId!, payload)
      } else {
        await updateTruck(carrierId!, truckId, payload)
      }

      void navigate(`/carriers/${carrierId}`, { replace: true })
    } catch (caught) {
      setErrors(toTruckFormErrors(caught))
    } finally {
      setIsSubmitting(false)
    }
  }

  /** Carriers › the carrier › this record — or the page's own title when the record cannot be read. */
  const trail = (leaf: string | null): Crumb[] => [
    { label: t('nav.carriers'), to: '/carriers' },
    { label: carrierName, to: `/carriers/${carrierId}` },
    { label: leaf },
  ]

  if (isLoading) {
    return <p className="session-check">{t('carriers.loading')}</p>
  }

  if (loadFailure !== null) {
    return (
      <section className="page">
        <Breadcrumbs trail={trail(t('carriers.truckEditTitle'))} />
        <header className="page__header">
          <h1>{t('carriers.truckEditTitle')}</h1>
        </header>
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'carriers.loadError')}
        </p>
        <p>
          <Link to={`/carriers/${carrierId}`}>{t('carriers.backToCarrier')}</Link>
        </p>
      </section>
    )
  }

  // A refusal that names a record links to it: the posting that holds the truck's kind.
  const field = (name: keyof TruckForm | 'features'): ReactNode => {
    const code = errors.fields[name]

    if (code === undefined) {
      return undefined
    }

    const subject = errors.subjects?.[code]
    const sentence = messageFor(code, t, 'carriers.saveError', subject)

    return subject === undefined ? (
      sentence
    ) : (
      <>
        {sentence} <RecordLink subject={subject} />
      </>
    )
  }

  // This truck as it is saved, or the step a new one takes.
  const leaf = !isEditing
    ? t('breadcrumbs.newTruck')
    : savedName === null
      ? null
      : t('breadcrumbs.truck', { unit: savedName })

  return (
    <section className="page">
      <Breadcrumbs trail={trail(leaf)} />
      <header className="page__header">
        <h1>{isEditing ? t('carriers.truckEditTitle') : t('carriers.truckTitle')}</h1>
      </header>

      {errors.summary.includes(CHANGED_ELSEWHERE) ? (
        <ChangedElsewhereNotice onLoadSaved={() => void loadWhatIsSaved()} isLoading={isLoadingSaved} />
      ) : (
        errors.summary.length > 0 && (
          <div className="alert" role="alert">
            {errors.summary.map((code) => (
              <p key={code}>{messageFor(code, t, 'carriers.saveError')}</p>
            ))}
          </div>
        )
      )}

      <form className="form form--stacked" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <fieldset className="fieldset">
          <legend>{t('carriers.identity')}</legend>

          <FormField
            name="unitNumber"
            label={t('carriers.unitNumber')}
            value={form.unitNumber}
            onChange={(value) => set('unitNumber', value)}
            error={field('unitNumber')}
            maxLength={32}
            required
            width="narrow"
          />
          <FormSelect
            name="type"
            label={t('carriers.truckType')}
            value={form.type}
            onChange={(value) => set('type', value)}
            // A truck saved before cabs were asked (TRKC-102) says so, and its save asks for one.
            // The fleet table's word: a longer one is cut by the select's width (found by looking).
            options={[
              ...(form.type === '' ? [{ value: '', label: t('carriers.notStated'), disabled: true }] : []),
              ...TRUCK_TYPES.map((type) => ({
                value: type,
                label: t(`carriers.truckTypes.${type}`),
              })),
            ]}
            error={field('type')}
            required
          />
          {form.type !== STRAIGHT_BOX_TRUCK && <p className="form__note">{t('carriers.cabHint')}</p>}
          <FormField
            name="make"
            label={t('carriers.make')}
            value={form.make}
            onChange={(value) => set('make', value)}
            error={field('make')}
            maxLength={64}
          />
          <FormField
            name="model"
            label={t('carriers.model')}
            value={form.model}
            onChange={(value) => set('model', value)}
            error={field('model')}
            maxLength={64}
          />
          <FormField
            name="vin"
            label={t('carriers.vin')}
            value={form.vin}
            // Uppercased as typed — VINs and plates are printed that way, and the
            // screen shows the spelling the domain stores.
            onChange={(value) => set('vin', value.toUpperCase())}
            error={field('vin')}
            maxLength={17}
          />
          <FormField
            name="licensePlate"
            label={t('carriers.licensePlate')}
            value={form.licensePlate}
            onChange={(value) => set('licensePlate', value.toUpperCase())}
            error={field('licensePlate')}
            maxLength={16}
            width="narrow"
          />
          {/* A straight box truck carries its own load, so it states what a trailer states for
              any other truck (TRKC-100); the server judges every save. */}
          {form.type === STRAIGHT_BOX_TRUCK && (
            <>
              <p className="form__note">{t('carriers.boxTruckHint')}</p>
              <FormSelect
                name="boxLength"
                label={t('carriers.boxLength')}
                value={form.boxLength}
                onChange={(value) => set('boxLength', value)}
                options={[
                  { value: '', label: t('carriers.boxLengthChoose'), disabled: true },
                  ...BOX_LENGTHS.map((feet) => ({ value: String(feet), label: t('carriers.feet', { feet }) })),
                ]}
                error={field('boxLength')}
                required
                width="narrow"
              />
              <FormField
                name="palletCapacity"
                label={t('carriers.palletCapacity')}
                value={form.palletCapacity}
                onChange={(value) => set('palletCapacity', keepDecimal(form.palletCapacity, value))}
                error={field('palletCapacity')}
                inputMode="decimal"
                width="narrow"
              />
              <FormField
                name="weightCapacity"
                label={t('carriers.weightCapacity')}
                value={form.weightCapacity}
                onChange={(value) => set('weightCapacity', keepDecimal(form.weightCapacity, value))}
                error={field('weightCapacity')}
                inputMode="decimal"
                width="narrow"
              />
              <div className="field field--choices" role="group" aria-labelledby="truck-features-label">
                <span id="truck-features-label">{t('carriers.features')}</span>
                {featuresAllowedOn(STRAIGHT_BOX_TRUCK).map((feature) => (
                  <FormCheckbox
                    key={feature}
                    name={`feature-${feature}`}
                    label={t(`equipment.features.${feature}`)}
                    checked={features.includes(feature)}
                    onChange={(checked) => toggleFeature(feature, checked)}
                  />
                ))}
                {field('features') !== undefined && (
                  <span className="field__error" role="alert">
                    {field('features')}
                  </span>
                )}
              </div>
            </>
          )}
        </fieldset>

        <div className="form__actions">
          <Link className="button button--quiet" to={`/carriers/${carrierId}`}>
            {t('carriers.backToCarrier')}
          </Link>
          <button type="submit" className="button" disabled={isSubmitting}>
            {isSubmitting ? t('carriers.saving') : t('carriers.saveTruck')}
          </button>
        </div>
      </form>

      {/* Registration and inspection papers live on the vehicle they cover — and
          filing one can move its standing, so the note below is re-read when it does. */}
      {truckId !== undefined && (
        <DocumentsPanel
          ownerType="Truck"
          ownerId={truckId}
          onOwnerStandingChanged={() => void refreshStanding()}
        />
      )}
      {truckId !== undefined && (
        <>
          {standing !== null && (
            <FleetAvailabilityNote
              record={standing.record}
              carrier={standing.carrier}
            />
          )}
          {standingFailure !== null && (
            <p className="alert alert--warning" role="status">
              {apiErrorMessage(standingFailure.error, t, 'compliance.standingUnread')}
            </p>
          )}
          <ActivationPanel
            ownerType="Truck"
            ownerId={truckId}
            active={activation.active}
            deactivatedAt={activation.deactivatedAt}
            onChanged={(active, deactivatedAt) => {
              setActivation({ active, deactivatedAt })
              void refreshStanding()
            }}
          />
        </>
      )}
    </section>
  )
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
